import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';

import { TotemInput } from 'components/TotemInput';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';

import { PAGES } from 'constants/pages';
import { useAuthenticateUserResetMutation, useSetPasswordMutation } from 'data/__generated__';
import { isTotadminUser } from 'utils/auth';

const MEDIUM_PASSWORD_REGEX = /^(((?=.*[a-z])(?=.*[A-Z]))|((?=.*[a-z])(?=.*[0-9]))|((?=.*[A-Z])(?=.*[0-9])))(?=.{6,})/;

export const ResetPassword = () => {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();

    const [password, setPassword] = useState('');
    const [passwordConfirmation, setPasswordConfirmation] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null);

    const [authenticateUserReset] = useAuthenticateUserResetMutation();
    const [setUserPassword] = useSetPasswordMutation();

    useEffect(() => {
        const verifyToken = async () => {
            if (!token) {
                setError('Lien invalide');
                setIsTokenValid(false);
                return;
            }
            try {
                const { data } = await authenticateUserReset({ variables: { token } });
                const result = data?.authenticateUserReset;
                if (result?.success) {
                    setIsTokenValid(true);
                } else {
                    setError(result?.error || 'Une erreur est survenue');
                    setIsTokenValid(false);
                }
            } catch (e: unknown) {
                const message = e instanceof Error ? e.message : '';
                if (message.includes('invalid token')) {
                    setError('Ce lien est invalide. Veuillez en demander un nouveau.');
                } else if (message.includes('maxAge exceeded')) {
                    setError('Ce lien a expiré. Veuillez en demander un nouveau.');
                } else {
                    setError('Une erreur est survenue lors de la vérification du lien.');
                }
                setIsTokenValid(false);
            }
        };

        verifyToken();
    }, [token, authenticateUserReset]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setFormError(null);

        if (password !== passwordConfirmation) {
            setFormError('Les mots de passe sont différents');
            return;
        }

        if (!password.match(MEDIUM_PASSWORD_REGEX)) {
            setFormError(
                'Le mot de passe doit contenir au moins 6 caractères avec un mix de majuscules, minuscules ou chiffres',
            );
            return;
        }

        const { data } = await setUserPassword({
            variables: { password, token: token! },
        });

        const response = data?.setUserPassword;

        if (response?.success && response.user) {
            if (isTotadminUser(response.user)) {
                navigate(PAGES.passwordDefined.url);
                return;
            }
            toast.success('Mot de passe réinitialisé avec succès');
            navigate('/');
        } else if (response?.error) {
            setFormError(response.error);
        }
    };

    if (isTokenValid === null) {
        return (
            <Container>
                <Helmet title="Chargement... · TOTADMIN" />
                <Title>Vérification en cours...</Title>
            </Container>
        );
    }

    if (!isTokenValid) {
        return (
            <Container>
                <Helmet title="Erreur · TOTADMIN" />
                <ErrorTitle>{error}</ErrorTitle>
                <BackLink to="/recover-password">Demander un nouveau lien</BackLink>
            </Container>
        );
    }

    return (
        <Container>
            <Helmet title="Réinitialiser mon mot de passe · TOTADMIN" />
            <Title>Réinitialiser mon mot de passe</Title>
            <ResetPasswordForm onSubmit={handleSubmit}>
                <TotemInput
                    value={password}
                    placeholder="Nouveau mot de passe"
                    type="password"
                    onChange={(value) => setPassword(value)}
                    label="Mot de passe"
                    dataTest="reset-password"
                />
                <TotemInput
                    value={passwordConfirmation}
                    placeholder="Confirmez votre mot de passe"
                    type="password"
                    onChange={(value) => setPasswordConfirmation(value)}
                    label="Confirmation"
                    dataTest="reset-password-confirm"
                />
                {formError ? <ErrorMessage>{formError}</ErrorMessage> : null}
                <SubmitButton type="submit" data-test="reset-password-submit">
                    Valider
                </SubmitButton>
            </ResetPasswordForm>
        </Container>
    );
};

const Container = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Title = styled.h1`
    text-transform: uppercase;
    color: ${({ theme }) => theme.textColor};
    letter-spacing: 0.7px;
    font-size: 40px;
`;

const ErrorTitle = styled.h1`
    color: ${({ theme }) => theme.errorColor};
    font-size: 24px;
    text-align: center;
    max-width: 500px;
`;

const ErrorMessage = styled.span`
    color: ${({ theme }) => theme.errorColor};
`;

const ResetPasswordForm = styled.form`
    display: flex;
    flex-direction: column;
    width: 400px;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;

const SubmitButton = styled(TotemPrimaryButton)`
    margin-top: 15px;
`;

const BackLink = styled(Link)`
    color: ${({ theme }) => theme.ctaPrimaryTextColor};
    margin-top: 20px;
`;
